import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing } from '../../design-system/tokens/spacing';
import { borders } from '../../design-system/tokens/borders';
import { shadows } from '../../design-system/tokens/shadows';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { generateGradient, darken } from '../../utils/colorUtils';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type AvatarStatus = 'online' | 'offline' | 'away' | 'busy';

interface AvatarProps {
  src?: string | null;
  alt?: string;
  name?: string;
  size?: AvatarSize;
  status?: AvatarStatus;
  border?: boolean;
  borderColor?: 'primary' | 'secondary' | 'accent';
  className?: string;
  onClick?: () => void;
}

const AvatarContainer = styled.div<{
  size: AvatarSize;
  border: boolean;
  borderColor: string;
  clickable: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  ${props => props.clickable && 'cursor: pointer;'}

  ${props => {
    const sizes = {
      xs: '24px',
      sm: '32px',
      md: '40px',
      lg: '56px',
      xl: '80px',
      '2xl': '120px',
    };
    return `
      width: ${sizes[props.size]};
      height: ${sizes[props.size]};
    `;
  }}

  ${props => props.border && `
    border: 2px solid ${props.borderColor};
    box-shadow: ${props.theme.shadows?.sm || shadows.sm};
  `}

  transition: all 0.2s ease;

  &:hover {
    ${props => props.clickable && `
      transform: scale(1.05);
      box-shadow: ${props.theme.shadows?.md || shadows.md};
    `}
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div<{
  size: AvatarSize;
  gradient: string;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.gradient};
  color: white;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    const sizes = {
      xs: '10px',
      sm: '12px',
      md: '14px',
      lg: '18px',
      xl: '24px',
      '2xl': '32px',
    };
    return `font-size: ${sizes[props.size]};`;
  }}
`;

const AvatarIcon = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.surfaceHover};
  color: ${props => props.theme.colors.textMuted};
`;

const StatusIndicator = styled.div<{
  status: AvatarStatus;
  size: AvatarSize;
}>`
  position: absolute;
  bottom: 0;
  right: 0;
  border-radius: 50%;
  border: 2px solid ${props => props.theme.colors.surface};
  
  ${props => {
    const sizes = {
      xs: '6px',
      sm: '8px',
      md: '10px',
      lg: '12px',
      xl: '14px',
      '2xl': '16px',
    };
    return `
      width: ${sizes[props.size]};
      height: ${sizes[props.size]};
    `;
  }}

  ${props => {
    const statusColors = {
      online: props.theme.colors.success,
      offline: props.theme.colors.textMuted,
      away: props.theme.colors.warning,
      busy: props.theme.colors.error,
    };
    return `background-color: ${statusColors[props.status]};`;
  }}
`;

const getInitials = (name?: string): string => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getGradientForName = (name?: string, theme?: any): string => {
  // Formal ICOM palette — navy / deep blue only (no warm gold/orange accents)
  const primary = theme?.colors?.primary || theme?.colors?.icomBlue || '#0A4F8A';
  const navy = theme?.colors?.icomNavy || theme?.colors?.primaryDark || '#1B2430';
  const deepBlue = theme?.colors?.primaryDark || darken(primary, 12);

  if (!name) {
    return generateGradient(primary, navy);
  }

  // Stable index from name so each profile gets a consistent formal shade
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const variants = [
    generateGradient(primary, navy),
    generateGradient(navy, primary),
    generateGradient(deepBlue, navy),
    generateGradient(darken(primary, 8), navy),
  ];
  return variants[Math.abs(hash) % variants.length];
};

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  name,
  size = 'md',
  status,
  border = false,
  borderColor = 'primary',
  className,
  onClick,
}) => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  
  const borderColorMap = {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    accent: theme.colors.accent,
  };

  const initials = getInitials(name);
  const gradient = getGradientForName(name, theme);
  const showImage = src && !imageError;

  return (
    <AvatarContainer
      size={size}
      border={border}
      borderColor={borderColorMap[borderColor]}
      clickable={!!onClick}
      className={className}
      onClick={onClick}
    >
      {showImage ? (
        <AvatarImage
          src={src}
          alt={alt || name || 'Avatar'}
          onError={() => setImageError(true)}
        />
      ) : initials ? (
        <AvatarFallback size={size} gradient={gradient}>
          {initials}
        </AvatarFallback>
      ) : (
        <AvatarIcon>
          <UserCircleIcon style={{ width: '100%', height: '100%' }} />
        </AvatarIcon>
      )}
      {status && <StatusIndicator status={status} size={size} />}
    </AvatarContainer>
  );
};

export default Avatar;
